import { useState, useEffect } from 'react'
import { FiX, FiLoader, FiSave } from 'react-icons/fi'
import axios from 'axios'
import toast from 'react-hot-toast'

export default function HeaderStylingModal({ isOpen, onClose, website, onSuccess }) {
  const [formData, setFormData] = useState({
    backgroundColor: '#FFFFFF',
    textColor: '#000000',
    hoverBackgroundColor: '#F3F4F6',
    hoverTextColor: '#374151',
    positioning: 'default'
  })
  const [saving, setSaving] = useState(false)
  const [isPreviewHovered, setIsPreviewHovered] = useState(false)

  useEffect(() => {
    if (website && isOpen) {
      setFormData({
        backgroundColor: website.headerStyling?.backgroundColor || '#FFFFFF',
        textColor: website.headerStyling?.textColor || '#000000',
        hoverBackgroundColor: website.headerStyling?.hoverBackgroundColor || '#F3F4F6',
        hoverTextColor: website.headerStyling?.hoverTextColor || '#374151',
        positioning: website.headerStyling?.positioning === 'next-section' ? 'next-section' : 'default'
      })
    }
  }, [website, isOpen])

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    setSaving(true)
    try {
      await axios.put(`/websites/${website._id}/header-styling`, formData)
      toast.success('Header styling updated successfully')
      if (onSuccess) onSuccess()
      onClose()
    } catch (error) {
      console.error('Error updating header styling:', error)
      toast.error(error.response?.data?.message || 'Failed to update header styling')
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 bg-blue-100 rounded flex items-center justify-center">
              <span className="text-blue-600 text-sm">🎨</span>
            </div>
            <h2 className="text-xl font-bold text-gray-900">Header Styling</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Form */}
        <div className="flex-1 overflow-y-auto">
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Customize Header Appearance</h3>
            <p className="text-sm text-gray-600 mb-6">
              Change the background and text colors of your website's header navigation bar.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Header Positioning</label>
            <select
              value={formData.positioning}
              onChange={(e) => handleInputChange('positioning', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="default">Default Positioning</option>
              <option value="next-section">Position Header in Next Section</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Overlay the header at the top of the first published section while preserving its responsive height.
            </p>
          </div>

          {/* Background Color */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Background Color
            </label>
            <div className="flex gap-2">
              <input
                type="color"
                value={formData.backgroundColor}
                onChange={(e) => handleInputChange('backgroundColor', e.target.value)}
                className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
              />
              <input
                type="text"
                value={formData.backgroundColor}
                onChange={(e) => handleInputChange('backgroundColor', e.target.value)}
                placeholder="#FFFFFF"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Choose the background color for the header
            </p>
          </div>

          {/* Text Color */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Text Color
            </label>
            <div className="flex gap-2">
              <input
                type="color"
                value={formData.textColor}
                onChange={(e) => handleInputChange('textColor', e.target.value)}
                className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
              />
              <input
                type="text"
                value={formData.textColor}
                onChange={(e) => handleInputChange('textColor', e.target.value)}
                placeholder="#000000"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Choose the text color for all header elements
            </p>
          </div>

          {/* Hover Background Color */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Hover Background Color
            </label>
            <div className="flex gap-2">
              <input
                type="color"
                value={formData.hoverBackgroundColor}
                onChange={(e) => handleInputChange('hoverBackgroundColor', e.target.value)}
                className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
              />
              <input
                type="text"
                value={formData.hoverBackgroundColor}
                onChange={(e) => handleInputChange('hoverBackgroundColor', e.target.value)}
                placeholder="#F3F4F6"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Choose the background color when hovering over the header
            </p>
          </div>

          {/* Hover Text Color */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Hover Text Color
            </label>
            <div className="flex gap-2">
              <input
                type="color"
                value={formData.hoverTextColor}
                onChange={(e) => handleInputChange('hoverTextColor', e.target.value)}
                className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
              />
              <input
                type="text"
                value={formData.hoverTextColor}
                onChange={(e) => handleInputChange('hoverTextColor', e.target.value)}
                placeholder="#374151"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Choose the text color when hovering over the header
            </p>
          </div>

          {/* Preview */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preview
            </label>
            <div
              className="border border-gray-300 rounded-lg p-4 cursor-pointer transition-colors"
              style={{
                backgroundColor: isPreviewHovered ? formData.hoverBackgroundColor : formData.backgroundColor,
                color: isPreviewHovered ? formData.hoverTextColor : formData.textColor
              }}
              onMouseEnter={() => setIsPreviewHovered(true)}
              onMouseLeave={() => setIsPreviewHovered(false)}
            >
              <div className="flex items-center justify-between">
                <div className="font-bold">Your Website Name</div>
                <div className="flex gap-4 text-sm">
                  <span>Home</span>
                  <span>About</span>
                  <span>Contact</span>
                </div>
              </div>
              <div className="text-center text-xs text-gray-400 mt-2">
                {isPreviewHovered ? 'Hover effect active' : 'Hover to preview hover colors'}
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex gap-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <FiLoader className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <FiSave className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  )
}